import api from './api';

export type AeroCrsApiStatus = {
  configured: boolean;
  healthy?: boolean;
  queueBusy?: boolean;
  currentOperation?: string | null;
  pendingOperations?: number;
  status?: 'NOT_CONFIGURED' | 'HEALTHY' | 'ERROR';
  message?: string;
  baseUrl: string;
  airlineCode: string;
  companyName: string;
  hasAuthId: boolean;
  hasAuthPassword: boolean;
  airlinesProbeSuccess?: boolean;
  matchedAirlineCompanyCode?: string | null;
  matchedAirlineName?: string | null;
  matchedAirlineIataCode?: string | null;
  matchedAirlineIcaoCode?: string | null;
  airlineRequiredPassengerFields?: string[];
  acceptsApiCreditCardPayments?: boolean | null;
  supportsBrandedFares?: boolean | null;
  warning?: string | null;
  probe?: {
    endpoint: string;
    statusCode: number;
    durationMs: number;
  } | null;
};

export type AeroCrsDestination = {
  code: string;
  name: string;
  iataCode?: string | null;
  icaoCode?: string | null;
  country?: string | null;
  countryIso?: string | null;
  destinationId?: string | null;
};

export type AeroCrsAgency = {
  id: string;
  name: string;
};

export type AeroCrsAirline = {
  companyName: string;
  companyCode?: string | null;
  iataCode?: string | null;
  icaoCode?: string | null;
  country?: string | null;
  mandatoryAge?: boolean | null;
  mandatoryPhone?: boolean | null;
  mandatoryNationality?: boolean | null;
  mandatoryDocumentNumber?: boolean | null;
  mandatoryDateOfBirth?: boolean | null;
  acceptsApiCreditCardPayments?: boolean | null;
  supportsBrandedFares?: boolean | null;
  rawPayload?: any;
};

export type AeroCrsFlight = {
  flightId: string;
  fareId?: string | null;
  airlineCode?: string | null;
  airlineName?: string | null;
  companyName?: string | null;
  flightNumber?: string | null;
  origin?: string | null;
  destination?: string | null;
  flightDate?: string | null;
  departureTime?: string | null;
  arrivalTime?: string | null;
  availableSeats?: number | null;
  freeSeats?: number | null;
  aircraft?: string | null;
  duration?: string | null;
  detailText?: string | null;
  fare?: number | null;
  currency?: string | null;
  status?: string | null;
  chargeType?: string | null;
  rawPayload?: any;
};

export type AeroCrsPassengerInput = {
  firstName: string;
  lastName: string;
  type?: 'ADULT' | 'CHILD' | 'INFANT';
  gender?: string;
  dateOfBirth?: string;
  nationality?: string;
  passportNumber?: string;
  phone?: string;
  email?: string;
};

export type AeroCrsCustomerSearchResult = {
  customerId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  mobileNumber?: string | null;
  address?: string | null;
  company?: string | null;
  displayName?: string | null;
};

export type AeroCrsSelectedCustomer = {
  customerId: string;
  snippet?: string;
  lines?: string[];
};

export type AeroCrsBooking = {
  bookingId?: string | null;
  bookingReference: string;
  airlineCode?: string | null;
  airlineName?: string | null;
  companyName?: string | null;
  bookingName?: string | null;
  status?: string | null;
  flightNumber?: string | null;
  flightDate?: string | null;
  origin?: string | null;
  destination?: string | null;
  departureTime?: string | null;
  arrivalTime?: string | null;
  availableSeats?: number | null;
  currency?: string | null;
  totalPrice?: number | null;
  fareBase?: number | null;
  taxAmount?: number | null;
  ticketTimeLimit?: string | null;
  paymentTimeLimit?: string | null;
  passengers?: AeroCrsPassengerInput[];
  customer?: AeroCrsSelectedCustomer | null;
  rawPayload?: any;
};

export type AeroCrsGeneratedDocument = {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  dataBase64: string;
  documentType: 'Ticket' | 'Invoice' | 'Itinerary';
  bookingConfirmation: string;
  passengerLastName: string;
  groupName?: string;
};

const unwrap = <T>(res: any, key?: string): T => {
  if (key) return (res?.data?.data?.[key] ?? res?.data?.[key]) as T;
  return (res?.data?.data ?? res?.data) as T;
};

export const aerocrsService = {
  async getStatus(): Promise<AeroCrsApiStatus> {
    const res = await api.get('/integrations/aerocrs-browser/status');
    return unwrap<AeroCrsApiStatus>(res);
  },

  async getDestinations(): Promise<AeroCrsDestination[]> {
    const res = await api.get('/integrations/aerocrs-browser/destinations');
    return unwrap<AeroCrsDestination[]>(res, 'destinations') || [];
  },

  async getAgencies(): Promise<AeroCrsAgency[]> {
    const res = await api.get('/integrations/aerocrs-browser/agencies');
    return unwrap<AeroCrsAgency[]>(res, 'agencies') || [];
  },

  async getAirlines(): Promise<AeroCrsAirline[]> {
    const res = await api.get('/integrations/aerocrs-browser/status');
    return unwrap<AeroCrsAirline[]>(res, 'airlines') || [];
  },

  async searchFlights(params: {
    from: string;
    to: string;
    departureDate: string;
    returnDate?: string;
    adults: number;
    children?: number;
    infants?: number;
    companyName?: string;
    flightNumber?: string;
    currency?: string;
    chargeType?: string;
    cabinCode?: string;
  }): Promise<{ flights: AeroCrsFlight[]; searchedAt?: string }> {
    const res = await api.get('/integrations/aerocrs-browser/flights', { params });
    return {
      flights: unwrap<AeroCrsFlight[]>(res, 'flights') || [],
      searchedAt: res?.data?.data?.searchedAt,
    };
  },

  async getFlight(params: {
    flightId?: string;
    fareId?: string;
    from?: string;
    to?: string;
    departureDate?: string;
    currency?: string;
    chargeType?: string;
    adults?: number;
    children?: number;
    infants?: number;
  }): Promise<AeroCrsFlight | null> {
    const res = await api.get('/integrations/aerocrs-browser/flights', { params });
    return unwrap<AeroCrsFlight | null>(res, 'flight') || null;
  },

  async createBookingDraft(payload: {
    tripType: 'OW' | 'RT';
    companyName: string;
    bookingName: string;
    flightType: 'SCHEDULED' | 'CHARTER';
    from: string;
    to: string;
    departureDate: string;
    returnDate?: string;
    adults: number;
    children?: number;
    infants?: number;
    currency?: string;
    chargeType: string;
    selectedFlight: AeroCrsFlight;
    passengers: AeroCrsPassengerInput[];
    ticketTimeLimit?: { date: string; time: string };
    paymentTimeLimit?: { date: string; time: string };
  }): Promise<AeroCrsBooking> {
    const res = await api.post('/integrations/aerocrs-browser/bookings/draft', payload);
    return unwrap<AeroCrsBooking>(res, 'booking');
  },

  async updateBookingLimits(reference: string, payload: {
    ticketTimeLimit?: { date: string; time: string };
    paymentTimeLimit?: { date: string; time: string };
  }): Promise<AeroCrsBooking> {
    const res = await api.post(`/integrations/aerocrs-browser/bookings/${encodeURIComponent(reference)}/limits`, payload);
    return unwrap<AeroCrsBooking>(res, 'booking');
  },

  async getBooking(reference: string): Promise<AeroCrsBooking> {
    const res = await api.get(`/integrations/aerocrs-browser/bookings/${encodeURIComponent(reference)}`);
    return unwrap<AeroCrsBooking>(res, 'booking');
  },

  async searchBookingCustomers(reference: string, query: string): Promise<AeroCrsCustomerSearchResult[]> {
    const res = await api.get(`/integrations/aerocrs-browser/bookings/${encodeURIComponent(reference)}/customers/search`, {
      params: { query },
    });
    return unwrap<AeroCrsCustomerSearchResult[]>(res, 'customers') || [];
  },

  async selectBookingCustomer(reference: string, customerId: string): Promise<{ booking: AeroCrsBooking | null; selectedCustomer: AeroCrsSelectedCustomer | null }> {
    const res = await api.post(`/integrations/aerocrs-browser/bookings/${encodeURIComponent(reference)}/customers/select`, {
      customerId,
    });
    return {
      booking: unwrap<AeroCrsBooking | null>(res, 'booking') || null,
      selectedCustomer: unwrap<AeroCrsSelectedCustomer | null>(res, 'selectedCustomer') || null,
    };
  },

  async generateDocument(payload: {
    bookingConfirmation: string;
    passengerLastName: string;
    groupName?: string;
    documentType: 'Ticket' | 'Invoice' | 'Itinerary';
  }): Promise<AeroCrsGeneratedDocument> {
    const res = await api.post('/integrations/aerocrs/documents/generate', payload);
    return unwrap<AeroCrsGeneratedDocument>(res);
  },
};
